import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import { getSettings, saveSetting, logActivity } from "@/lib/cms";
import { applySeo } from "@/lib/seo";
import { useToast } from "@/context/ToastContext";
import { useSiteContent } from "@/context/SiteContentContext";
import type { Project } from "@/lib/types";
import { PageHeader, Button, Card, Spinner, Field, Input, Textarea } from "@/pages/admin/components/ui";
import ImageUploader from "@/pages/admin/components/ImageUploader";

export default function Seo() {
  const { success, error: toastError } = useToast();
  const { refresh } = useSiteContent();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [published, setPublished] = useState<Project[]>([]);
  const [form, setForm] = useState({
    seo_title: "",
    seo_description: "",
    seo_keywords: "",
    seo_canonical: "",
    seo_og_image: "",
    seo_twitter_image: "",
    seo_robots: "index, follow",
    favicon_url: "",
  });

  useEffect(() => {
    getSettings().then((s) => {
      setForm({
        seo_title: String(s.seo_title ?? ""),
        seo_description: String(s.seo_description ?? ""),
        seo_keywords: String(s.seo_keywords ?? ""),
        seo_canonical: String(s.seo_canonical ?? ""),
        seo_og_image: String(s.seo_og_image ?? ""),
        seo_twitter_image: String(s.seo_twitter_image ?? ""),
        seo_robots: String(s.seo_robots ?? "index, follow"),
        favicon_url: String(s.favicon_url ?? ""),
      });
      setLoading(false);
    });
    supabase.from("projects").select("slug, nombre").eq("estado", "publicado").order("orden").then(({ data }) => setPublished((data ?? []) as Project[]));
  }, []);

  function set<K extends keyof typeof form>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function save() {
    setSaving(true);
    try {
      const entries = Object.entries(form) as [string, string][];
      for (const [key, value] of entries) {
        await saveSetting(key, value);
      }
      applySeo(form);
      await logActivity("actualizó el SEO del sitio");
      success("SEO guardado correctamente.");
      refresh();
    } catch {
      toastError("No se pudieron guardar los cambios.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) return <Spinner label="Cargando SEO…" />;

  return (
    <div>
      <PageHeader
        title="SEO"
        subtitle="Metadatos y configuración para buscadores."
        actions={<Button onClick={save} disabled={saving}>{saving ? "Guardando…" : "Guardar cambios"}</Button>}
      />

      <div className="space-y-6">
        <Card className="p-6">
          <h3 className="text-sm font-semibold text-foreground-900 mb-4">Metadatos globales</h3>
          <div className="space-y-4">
            <Field label="SEO Title" hint="Máx. 60 caracteres recomendado.">
              <Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} />
            </Field>
            <Field label="Meta description" hint="120-160 caracteres recomendado.">
              <Textarea rows={3} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} />
            </Field>
            <Field label="Keywords" hint="Separadas por coma.">
              <Input value={form.seo_keywords} onChange={(e) => set("seo_keywords", e.target.value)} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="URL canónica"><Input value={form.seo_canonical} onChange={(e) => set("seo_canonical", e.target.value)} placeholder="https://…" /></Field>
              <Field label="Robots"><Input value={form.seo_robots} onChange={(e) => set("seo_robots", e.target.value)} /></Field>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-sm font-semibold text-foreground-900 mb-4">Imágenes y favicon</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <ImageUploader label="Open Graph image" value={form.seo_og_image} onChange={(v) => set("seo_og_image", v)} folder="seo" aspect="aspect-[16/9]" />
            <ImageUploader label="Twitter image" value={form.seo_twitter_image} onChange={(v) => set("seo_twitter_image", v)} folder="seo" aspect="aspect-[16/9]" />
            <ImageUploader label="Favicon" value={form.favicon_url} onChange={(v) => set("favicon_url", v)} folder="seo" aspect="aspect-square" />
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="text-sm font-semibold text-foreground-900 mb-4">Sitemap (generado de proyectos publicados)</h3>
          <p className="text-xs text-foreground-500 mb-4">El sitemap incluye la portada y cada proyecto publicado.</p>
          <div className="space-y-1.5 font-mono text-xs text-foreground-700">
            <p>/</p>
            {published.map((p) => (
              <p key={p.slug}>/proyecto/{p.slug}</p>
            ))}
            {published.length === 0 && <p className="text-foreground-400">No hay proyectos publicados.</p>}
          </div>
        </Card>
      </div>
    </div>
  );
}